import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';

import Global from '../../../../../../../store/global-selectors';
import type { LtlShipment } from '@shipstation/client-common/types/ltl';
import { WeightUnits } from '../../../../../../../prop-types/weight-unit';
import { FormattedWeight } from '../../../../../../../components/formatted-weight';
import { IntlUnitFormattingService } from '../../../../../../../services/intl-unit-formatting-services/intl-unit-formatting-service';
import { displayFreightClass } from '../../../../../constants';
import { confirmationCardMessages } from './confirmation-card-messages';

import style from './confirmation-cards.module.less';

type HandlingUnitsCardProps = {
  shipmentDetails: LtlShipment;
};

export const HandlingUnitsCard = ({ shipmentDetails }: HandlingUnitsCardProps) => {
  const intl = useIntl();
  const sellerDefaultDimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const handlingUnits = shipmentDetails.handlingUnits ?? [];

  if (handlingUnits.length === 0) {
    return (
      <div className={style['card']}>
        <div className={style['card-header']}>
          <Text isBold textType={TextType.H4} value={confirmationCardMessages.handlingUnitsHeader} />
        </div>
        <div className={style['card-body']}>
          <Text textType={TextType.Paragraph} value={confirmationCardMessages.noHandlingUnits} />
        </div>
      </div>
    );
  }

  return (
    <div className={style['card']}>
      <div className={style['card-header']}>
        <Text isBold textType={TextType.H4} value={confirmationCardMessages.handlingUnitsHeader} />
      </div>
      <div className={style['card-body']}>
        <FlexColumn gap={Gap.Space12}>
          {handlingUnits.map((unit) => (
            <FlexColumn key={unit.id} className={style['handling-unit-block']} gap={Gap.Space8}>
              <FlexColumn gap={Gap.Space4}>
                <Text
                  isBold
                  textType={TextType.Paragraph}
                  value={intl.formatMessage(confirmationCardMessages.handlingUnitDescription, {
                    type: unit.packagingType,
                    quantity: unit.quantity,
                  })}
                />
                <Text
                  textType={TextType.Caption}
                  value={IntlUnitFormattingService.formatDimensions(
                    { length: unit.length, width: unit.width, height: unit.height, unit: sellerDefaultDimensionUnit },
                    sellerDefaultDimensionUnit,
                  )}
                />
              </FlexColumn>
              {unit.commodities?.map((commodity) => (
                <FlexColumn key={commodity.id} gap={Gap.Space4}>
                  <Text
                    textType={TextType.Paragraph}
                    value={intl.formatMessage(confirmationCardMessages.commodityDescription, {
                      freightClass: displayFreightClass(commodity.freightClass),
                      description: commodity.description,
                    })}
                  />
                  <Text
                    textType={TextType.Caption}
                    value={<FormattedWeight weight={{ value: commodity.weight, unit: commodity.weightUnit || WeightUnits.Pounds }} />}
                  />
                </FlexColumn>
              ))}
            </FlexColumn>
          ))}
        </FlexColumn>
      </div>
    </div>
  );
};
